import React, { useEffect } from 'react';
import { Typography, Button, Modal } from '@douyinfe/semi-ui';
import { useDrop } from 'react-dnd';
import { IconDelete, IconCopy } from '@douyinfe/semi-icons';
import ModuleRenderer from './ModuleRenderer';
import styles from './DroppableCanvas.module.scss';
import DraggableModule from './DraggableModule';

const { Text } = Typography;

const DroppableCanvas = ({ modules, onDrop, onModuleEdit, onModuleDelete, onModulesReorder, onModuleSelect }) => {
    const [selectedModuleId, setSelectedModuleId] = React.useState(null);
    const [{ isOver }, drop] = useDrop(() => ({
        accept: 'H5_MODULE',
        drop: (item) => onDrop(item),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        })
    }));

    const handleModuleSelect = (module) => {
        setSelectedModuleId(module.id);
        // 确保传递完整的模块数据，包括当前的属性值
        const selectedModule = modules.find(m => m.id === module.id);
        if (selectedModule) {
            onModuleSelect?.(selectedModule);
        }
    };

    const showDeleteConfirm = (moduleId) => {
        const module = modules.find(m => m.id === moduleId);
        if (!module) return;

        Modal.confirm({
            title: '确认删除',
            content: <span>确定要删除<span style={{ color: 'var(--semi-color-danger)' }}>{module.title || module.name || ''}</span>模块吗？</span>,
            onOk: () => onModuleDelete(moduleId)
        });
    };

    const handleKeyDown = (e) => {
        if (!modules.length) return;

        const currentIndex = modules.findIndex(m => m.id === selectedModuleId);
        let newIndex = currentIndex;

        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                newIndex = currentIndex > 0 ? currentIndex - 1 : modules.length - 1;
                handleModuleSelect(modules[newIndex]);
                break;
            case 'ArrowDown':
                e.preventDefault();
                newIndex = currentIndex < modules.length - 1 ? currentIndex + 1 : 0;
                handleModuleSelect(modules[newIndex]);
                break;
            case 'Delete':
                if (selectedModuleId) {
                    showDeleteConfirm(selectedModuleId);
                }
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedModuleId, modules]);

    // 确保弹幕模块在最前面，底部按钮始终在最后
    const sortedModules = React.useMemo(() => {
        const danmakuModules = modules.filter(m => m.type === 'DANMAKU');
        const nonBottomModules = modules.filter(m => m.type !== 'BOTTOM_BUTTON' && m.type !== 'DANMAKU');
        const bottomButtons = modules.filter(m => m.type === 'BOTTOM_BUTTON');
        return [...danmakuModules, ...nonBottomModules, ...bottomButtons];
    }, [modules]);

    return (
        <div className={styles.canvas}>
            <div className={styles.canvasInner} ref={drop}>
                {sortedModules.length > 0 ? (
                    sortedModules.map((module, index) => (
                        <DraggableModule
                            key={module.id}
                            id={module.id}
                            index={index}
                            onMove={(dragIndex, hoverIndex) => {
                                // 如果是底部按钮，禁止拖动
                                if (sortedModules[dragIndex].type === 'BOTTOM_BUTTON' || sortedModules[dragIndex].type === 'DANMAKU') return;

                                const newModules = [...modules];
                                // 将排序后的索引转换为原始模块数组中的索引
                                const originalDragIndex = modules.findIndex(m => m.id === sortedModules[dragIndex].id);
                                const dragModule = newModules[originalDragIndex];

                                // 确保底部按钮不会被其他模块插入到前面
                                const bottomButtonIndex = newModules.findIndex(m => m.type === 'BOTTOM_BUTTON');
                                if (bottomButtonIndex !== -1 && hoverIndex >= bottomButtonIndex) {
                                    return;
                                }

                                // 删除原位置的模块
                                newModules.splice(originalDragIndex, 1);

                                // 计算目标位置，确保不会插入到底部按钮前面
                                const targetIndex = Math.min(hoverIndex, bottomButtonIndex === -1 ? newModules.length : bottomButtonIndex);
                                newModules.splice(targetIndex, 0, dragModule);
                                onModulesReorder(newModules);
                            }}
                        >
                            <div className={`${styles.moduleWrapper} ${selectedModuleId === module.id ? styles.selected : ''}`}>
                                <div onClick={() => handleModuleSelect(module)}>
                                    <ModuleRenderer
                                        type={module.type}
                                        data={module.data}
                                        onEdit={onModuleEdit}
                                        onStyleChange={onModuleEdit}
                                        moduleId={module.id}
                                        style={module.style}
                                    />
                                </div>
                                <Button
                                    className={styles.deleteButton}
                                    icon={<IconDelete />}
                                    type="danger"
                                    theme="borderless"
                                    onClick={() => showDeleteConfirm(module.id)}
                                />
                                {module.type !== 'DANMAKU' && module.type !== 'BOTTOM_BUTTON' && (
                                    <Button
                                        className={styles.copyButton}
                                        icon={<IconCopy />}
                                        type="secondary"
                                        theme="borderless"
                                        onClick={() => {
                                            Modal.confirm({
                                                title: '确认复制',
                                                content: <span>确定要复制<span style={{ color: 'var(--semi-color-primary)' }}>{module.title || module.name || ''}</span>模块吗？</span>,
                                                onOk: () => {
                                                    const newModule = {
                                                        ...module,
                                                        id: Date.now(),
                                                    };
                                                    const newModules = [...modules];
                                                    const currentIndex = modules.findIndex(m => m.id === module.id);
                                                    newModules.splice(currentIndex + 1, 0, newModule);
                                                    onModulesReorder(newModules);
                                                }
                                            });
                                        }}
                                    />
                                )}
                            </div>
                        </DraggableModule>
                    ))
                ) : (
                    <div className={styles.placeholder} style={{ textAlign: 'center', marginTop: '40px' }}>
                        <Text type="tertiary">请从左侧拖入模块</Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DroppableCanvas;